/**
 * Clause mining dry runs — request, approve, run now, cancel, list, get ·
 * CLM-103.
 *
 * mine-clauses and match-clauses run from the web app the way the extraction
 * pipeline does (CLM-83, `pipeline-runs.ts`) — the same request → approve →
 * run → terminal shape, the same one-queue-slot rule — restricted to **dry
 * runs only**, of the mining `segment` / `cluster` / `describe` stages
 * (first slice, PR #181) and the `match` stage (second slice, this change).
 *
 * ## Why dry-run-only is not a missing feature but the reason this is safe
 *
 * `--dry-run` on `mine-clauses` reads, computes and prints the plan; it
 * writes nothing and calls no provider, for every stage including
 * `describe` — so a dry run spends no money, makes no FR-29 disclosure
 * decision and writes no row `clm.clause_mining_run`/`clause_cluster`/etc.
 * `--dry-run` on `match-clauses` is the same shape: `match-contract.ts`
 * partitions every paragraph and returns `'planned'` **before** stage ③'s
 * adjudication call or any write (see `match-contract.ts` around the
 * `if (input.dryRun) return 'planned';` line) — so a match dry run also
 * spends no money and writes nothing. A **live** run of either kind is a
 * different and larger decision — for mining, a stage-sequencing safeguard so
 * the UI cannot chain segment → cluster → describe unsupervised, the CLM-101
 * batch-statement-timeout property, and the untracked double-dispatch gap
 * between an API worker and the `mine-clauses.yml` break-glass CLI; for
 * matching, the same double-dispatch gap against `match-clauses.yml` plus
 * CLM-101's batch-timeout property has no matching-side counterpart yet — and
 * stays out of scope here. A live run of either kind stays on its GitHub
 * Actions workflow.
 *
 * `match-report` (the calibration report stage) is deliberately **not**
 * modelled here: its own CLI refuses to combine `--report` with any run flag
 * (`--dry-run`, `--limit`, `--contract-key`, `--rematch-unreviewed`), it
 * writes nothing to the database and calls no provider already, and it has no
 * request → approve → run → terminal lifecycle to fit into this table's
 * shape. It keeps running from `match-clauses.yml`'s `match-report` dispatch
 * input; pulling it into this mechanism would build UI for something that
 * structurally isn't a "run" in this table's sense.
 *
 * A run is an **org-level** object, exactly as `0092`'s `pipeline_run` is: no
 * `root_contract_key`, no scope predicate; the action check
 * (`clause_mining:request` / `clause_mining:approve` — unchanged names, now
 * read as "a mine-clauses or match-clauses dry run") is the gate.
 *
 * ## The filter is stage-shaped, not `pipeline_run`'s `{ limit }`
 *
 *   segment    limit (contracts, 1..500), contract_key (optional, narrows to one)
 *   cluster    paragraph_limit (optional; unset = the whole corpus)
 *   describe   top (clusters, 1..500)
 *   match      limit (contracts, optional; unset = CLM_MATCH_DEFAULT_LIMIT),
 *              contract_key (optional, narrows to one),
 *              rematch_unreviewed (optional; loop-3 re-evaluation)
 *
 * A request for a stage may only carry that stage's fields — enforced by the
 * discriminated union below, not by a shared `{ limit }` shape. `match`'s
 * `limit` has no fixed schema default (unlike `segment`'s): the CLI's own
 * default is `CLM_MATCH_DEFAULT_LIMIT`, an environment value this package
 * cannot see, so an absent `limit` is threaded through as absent and resolved
 * against `MatchConfig.defaultLimit` where the filter is turned into run
 * options — the same pattern `cluster`'s optional `paragraph_limit` already
 * uses for `MiningConfig.paragraphLimit`.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema } from '../primitives.js';
export const CLAUSE_MINING_DRY_RUN_STAGES = ['segment', 'cluster', 'describe', 'match'];
/** The mine-clauses stages — `runOptionsFor` / the mining worker's own claim. */
export const MINE_CLAUSES_DRY_RUN_STAGES = ['segment', 'cluster', 'describe'];
/** The match-clauses stage — currently just the one. */
export const MATCH_CLAUSES_DRY_RUN_STAGES = ['match'];
export const CLAUSE_MINING_DRY_RUN_STATUSES = [
    'requested',
    'approved',
    'running',
    'completed',
    'failed',
    'cancelled',
];
/** The statuses a run may still leave; everything else is terminal. */
export const CLAUSE_MINING_DRY_RUN_ACTIVE_STATUSES = ['requested', 'approved', 'running'];
export const CLAUSE_MINING_DRY_RUN_HISTORY_LIMIT = 20;
export const CLAUSE_MINING_DRY_RUN_REASON_MAX_LENGTH = 400;
/** The 409 the one-slot rule produces; rides in `field_errors[0].message`. */
export const CLAUSE_MINING_DRY_RUN_ALREADY_PENDING = 'dry_run_already_pending';
const LIMIT_MAX = 500;
export const ClauseMiningDryRunFilterSchema = z.discriminatedUnion('stage', [
    z
        .object({
        stage: z.literal('segment'),
        limit: z.number().int().min(1).max(LIMIT_MAX).default(20),
        contract_key: ContractKeySchema.optional(),
    })
        .strict(),
    z
        .object({
        stage: z.literal('cluster'),
        /** Unset = the whole corpus, which is the cluster stage's own default (see `MiningConfig.paragraphLimit`). */
        paragraph_limit: z.number().int().min(1).optional(),
    })
        .strict(),
    z
        .object({
        stage: z.literal('describe'),
        top: z.number().int().min(1).max(LIMIT_MAX).default(50),
    })
        .strict(),
    z
        .object({
        stage: z.literal('match'),
        /** No fixed default — see the module note. Resolved against `MatchConfig.defaultLimit` when absent. */
        limit: z.number().int().min(1).max(LIMIT_MAX).optional(),
        contract_key: ContractKeySchema.optional(),
        /** Loop 3 — re-evaluate every unreviewed row regardless of band. */
        rematch_unreviewed: z.boolean().optional(),
    })
        .strict(),
]);
/* -------------------------------------------------------------------------- */
/* The reported result — MineClausesSummary, reduced to its dry-run fields     */
/* -------------------------------------------------------------------------- */
/**
 * `describe_outcomes` is not carried here: `mine-clauses-runner.ts` guarantees
 * it is empty by construction on a dry run — `describeCluster` is never called
 * — so a schema for it would always validate an empty array. `segment_outcomes`
 * is carried in full: it is the segment stage's whole dry-run answer (per
 * contract: written/planned/failed/blocked_residue, paragraph and quality
 * counts), and rule 13 is respected — no clause text or span, only counts and
 * a bounded `weaknesses` string list `mine-clauses-runner.ts` already caps.
 */
export const ClauseMiningSegmentOutcomeSchema = z
    .object({
    contract_key: ContractKeySchema,
    status: z.enum(['written', 'planned', 'failed', 'blocked_residue']),
    paragraphs: z.number().int().min(0),
    embedded: z.number().int().min(0),
    quality: z.string().nullable(),
    weaknesses: z.array(z.string()).max(20),
    oversized: z.number().int().min(0),
    processing_restricted: z.boolean(),
    error: z.object({ name: z.string(), message: z.string() }).strict().nullable(),
})
    .strict();
/** The describe stage's candidate list — what it would send, before any of it costs a model call. */
export const ClauseMiningDescribeCandidateSchema = z
    .object({
    cluster_id: z.number().int(),
    cluster_no: z.number().int(),
    member_count: z.number().int().min(0),
    distinct_contract_count: z.number().int().min(0),
})
    .strict();
/**
 * The mine-clauses (`segment` / `cluster` / `describe`) dry-run result shape —
 * unchanged, byte-for-byte, from the first slice. Kept as its own schema
 * (rather than folded into a discriminated union with `match`'s shape) so any
 * already-persisted `clm.clause_mining_dry_run.result` row from before this
 * change still parses without a migration; see {@link ClauseMiningDryRunResultSchema}.
 */
export const ClauseMiningStageDryRunResultSchema = z
    .object({
    stage: z.enum(MINE_CLAUSES_DRY_RUN_STAGES),
    ok: z.boolean(),
    model_key: z.string(),
    normalise_rules_version: z.string(),
    join_threshold: z.number(),
    min_paragraph_chars: z.number().int(),
    max_document_repeats: z.number().int(),
    mining_run_id: z.number().int().nullable(),
    mining_run_reused: z.boolean(),
    contracts_seen: z.number().int().min(0),
    contracts_written: z.number().int().min(0),
    contracts_failed: z.number().int().min(0),
    paragraphs_written: z.number().int().min(0),
    paragraphs_embedded: z.number().int().min(0),
    quality_split: z.object({ structural: z.number().int().min(0), weak: z.number().int().min(0) }).strict(),
    clusters_formed: z.number().int().min(0),
    clusters_described: z.number().int().min(0),
    clusters_refused: z.number().int().min(0),
    clusters_skipped_singleton: z.number().int().min(0),
    clusters_withheld_restricted: z.number().int().min(0),
    segment_outcomes: z.array(ClauseMiningSegmentOutcomeSchema).max(LIMIT_MAX),
    describe_candidates: z.array(ClauseMiningDescribeCandidateSchema).max(LIMIT_MAX),
    stopped_early: z.object({ after: z.string(), reason: z.string() }).strict().nullable(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* The match-clauses dry-run result — MatchClausesSummary, reduced            */
/* -------------------------------------------------------------------------- */
/**
 * `ParagraphCounts`, restated — `match-contract.ts`'s own shape. Every field
 * is populated by stage ① + ② + partition, which a dry run always runs;
 * `adjudicated` / `unadjudicated` / `refused` / `held_residue` / `high` /
 * `medium` / `low` are the stage-③-and-write fields and are always `0` on a
 * dry run (partition never reaches them — `match-contract.ts`'s
 * `if (input.dryRun) return 'planned';`), kept rather than trimmed so this
 * schema matches the runner's actual return shape one-for-one instead of a
 * hand-picked subset that could silently drift from it.
 */
export const ClauseMatchingParagraphCountsSchema = z
    .object({
    candidates: z.number().int().min(0),
    settled_near_exact: z.number().int().min(0),
    values_differ: z.number().int().min(0),
    below_low: z.number().int().min(0),
    no_library_candidates: z.number().int().min(0),
    withheld_restricted: z.number().int().min(0),
    unembedded: z.number().int().min(0),
    adjudicated: z.number().int().min(0),
    unadjudicated: z.number().int().min(0),
    refused: z.number().int().min(0),
    held_residue: z.number().int().min(0),
    high: z.number().int().min(0),
    medium: z.number().int().min(0),
    low: z.number().int().min(0),
})
    .strict();
/** `ContractMatchOutcome.plan[]` — a paragraph's partition stage. No clause text or span — rule 13. */
export const CLAUSE_MATCHING_PARAGRAPH_STAGES = [
    'settled_near_exact',
    'values_differ',
    'below_low',
    'no_library_candidates',
    'withheld_restricted',
    'unembedded',
    'uncertain',
];
export const ClauseMatchingDryRunPlanItemSchema = z
    .object({
    contract_clause_id: z.number().int(),
    stage: z.enum(CLAUSE_MATCHING_PARAGRAPH_STAGES),
})
    .strict();
export const CLAUSE_MATCHING_CONTRACT_STATUSES = [
    'matched',
    'planned',
    'skipped_map_drift',
    'skipped_no_map_version',
    'skipped_no_candidates',
    'failed',
    'failed_output_budget',
];
/**
 * `ContractMatchOutcome`, reduced: `provider_calls` and `calls` are dropped —
 * both are guaranteed empty by construction on a dry run (adjudication never
 * runs), the same reasoning that drops `describe_outcomes` from the mining
 * shape above.
 */
export const ClauseMatchingDryRunOutcomeSchema = z
    .object({
    contract_key: ContractKeySchema,
    status: z.enum(CLAUSE_MATCHING_CONTRACT_STATUSES),
    processing_restricted: z.boolean(),
    counts: ClauseMatchingParagraphCountsSchema,
    library_residue: z.array(z.string()),
    plan: z.array(ClauseMatchingDryRunPlanItemSchema),
    error: z.object({ name: z.string(), message: z.string() }).strict().nullable(),
})
    .strict();
/**
 * `MatchClausesSummary`, reduced for a dry run: `dry_run` (always `true` here
 * — 0098's `ck_clause_mining_dry_run_dry_run_only`, restated for match rows)
 * and `provider_calls` are dropped for the same guaranteed-empty reason as
 * `ClauseMatchingDryRunOutcomeSchema.calls`.
 */
export const ClauseMatchingDryRunResultSchema = z
    .object({
    stage: z.literal('match'),
    ok: z.boolean(),
    correlation_id: z.string().uuid(),
    thresholds: z
        .object({ high_cosine: z.number(), high_margin: z.number(), low_cosine: z.number(), near_exact: z.number() })
        .strict(),
    top_k: z.number().int(),
    max_per_call: z.number().int(),
    library_clauses: z.number().int().min(0),
    contracts_seen: z.number().int().min(0),
    contracts_matched: z.number().int().min(0),
    contracts_failed: z.number().int().min(0),
    contracts_skipped: z.number().int().min(0),
    contracts_withheld_restricted: z.number().int().min(0),
    library_residue: z.array(z.string()),
    paragraphs: ClauseMatchingParagraphCountsSchema,
    outcomes: z.array(ClauseMatchingDryRunOutcomeSchema).max(LIMIT_MAX),
    stopped_early: z.object({ after: z.string(), reason: z.string() }).strict().nullable(),
})
    .strict();
/**
 * The reported `result` for any dry-run stage. A `z.union` rather than a
 * `z.discriminatedUnion` — the two members are not siblings of one
 * discriminated shape family, they are the pre-existing mining shape kept
 * verbatim (see {@link ClauseMiningStageDryRunResultSchema}) plus the new
 * match shape; `stage` still discriminates in practice (`'segment' |
 * 'cluster' | 'describe'` vs `'match'`) but zod validates a union by trying
 * each member, which is fine here — two members, a `.strict()` object each,
 * cheap to try both.
 */
export const ClauseMiningDryRunResultSchema = z.union([
    ClauseMiningStageDryRunResultSchema,
    ClauseMatchingDryRunResultSchema,
]);
/* -------------------------------------------------------------------------- */
/* The run                                                                     */
/* -------------------------------------------------------------------------- */
export const ClauseMiningDryRunSchema = z
    .object({
    clause_mining_dry_run_id: z.string().uuid(),
    stage: z.enum(CLAUSE_MINING_DRY_RUN_STAGES),
    status: z.enum(CLAUSE_MINING_DRY_RUN_STATUSES),
    requested_by_upn: z.string(),
    requested_utc: IsoTimestampUtcSchema,
    filter: ClauseMiningDryRunFilterSchema,
    approved_by_upn: z.string().nullable(),
    approved_utc: IsoTimestampUtcSchema.nullable(),
    started_utc: IsoTimestampUtcSchema.nullable(),
    heartbeat_utc: IsoTimestampUtcSchema.nullable(),
    finished_utc: IsoTimestampUtcSchema.nullable(),
    result: ClauseMiningDryRunResultSchema.nullable(),
    correlation_id: z.string().uuid(),
    error_summary: z.string().nullable(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* request-clause-mining-dry-run · run-clause-mining-dry-run-now               */
/* -------------------------------------------------------------------------- */
export const RequestClauseMiningDryRunRequestSchema = z
    .object({
    filter: ClauseMiningDryRunFilterSchema,
    reason: z.string().trim().min(1).max(CLAUSE_MINING_DRY_RUN_REASON_MAX_LENGTH),
})
    .strict();
export const RequestClauseMiningDryRunResponseSchema = z.object({ run: ClauseMiningDryRunSchema }).strict();
/** Same body as a request; the admin gate makes it create **and** approve in one action (mirrors D-83-3). */
export const RunClauseMiningDryRunNowRequestSchema = RequestClauseMiningDryRunRequestSchema;
export const RunClauseMiningDryRunNowResponseSchema = RequestClauseMiningDryRunResponseSchema;
/* -------------------------------------------------------------------------- */
/* approve · cancel · get — keyed by the path parameter                       */
/* -------------------------------------------------------------------------- */
export const ClauseMiningDryRunIdRequestSchema = z.object({ clause_mining_dry_run_id: z.string().uuid() }).strict();
export const ApproveClauseMiningDryRunResponseSchema = RequestClauseMiningDryRunResponseSchema;
export const CancelClauseMiningDryRunResponseSchema = RequestClauseMiningDryRunResponseSchema;
export const GetClauseMiningDryRunResponseSchema = RequestClauseMiningDryRunResponseSchema;
/* -------------------------------------------------------------------------- */
/* list-clause-mining-dry-runs                                                 */
/* -------------------------------------------------------------------------- */
export const ListClauseMiningDryRunsRequestSchema = z.object({}).strict();
export const ListClauseMiningDryRunsResponseSchema = z
    .object({
    /** The one `running` row, or null. */
    active: ClauseMiningDryRunSchema.nullable(),
    /** `requested` / `approved` rows — at most one under the one-slot rule, two admitted for the moment a claim is in flight. */
    pending: z.array(ClauseMiningDryRunSchema).max(2),
    /** The last twenty terminal rows, `finished_utc DESC`. */
    history: z.array(ClauseMiningDryRunSchema).max(CLAUSE_MINING_DRY_RUN_HISTORY_LIMIT),
})
    .strict();
//# sourceMappingURL=clause-mining-dry-runs.js.map